import { Button } from "@/components/button";
import { FileInput } from "@/components/file-input";
import { useProModal } from "@/components/pro-alert-box";
import { Table } from "@/components/table";
import { api } from "@/config/api-fetch";
import { KbType, useKnowledgeBase } from "@/hooks/use-knowledge-base";
import { useModal } from "@/hooks/use-modal";
import { IS_PRO } from "@/utils/constants";
import { css } from "@/utils/css";
import { useMemo, useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { toast } from "react-toastify";

export function PdfUpload() {
    const { data, refetch, isLoading } = useKnowledgeBase(KbType.pdf);
    const [pdf, setPdf] = useState<File>();
    const [submitting, setSubmitting] = useState(false);
    const { confirm } = useModal();
    const [deletingIds, setDeletingIds] = useState<string[]>([]);
    const { showProAlert } = useProModal();

    const deleteFromKb = async (id: string) => {
        setDeletingIds((prev) => [...prev, id]);

        try {
            await api.delete(`/knowledge-base?knowledge_id=${id}`);
            await refetch();
            toast.success(__("PDF has been deleted.", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setDeletingIds((prev) => prev.filter((prevId) => prevId !== id));
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirm(__("Do you want the AI to forget the information of this PDF?", "antimanual"));

        if (confirmed) {
            await deleteFromKb(id);
        }
    };

    const handleBulkDelete = async (ids: string[]) => {
        if (ids.length === 0) {
            toast.error(__("Please select at least one PDF.", "antimanual"));
            return;
        }

        const confirmed = await confirm(
            __("All of the selected PDFs will be removed from the AI's memory. Do you still want to do that?", "antimanual")
        );

        if (!confirmed) return;

        for (const id of ids) {
            await deleteFromKb(id);
        }
    };

    const columns = [
        { id: "file", label: __("File", "antimanual") },
        {
            id: "action",
            label: __("Action", "antimanual"),
        },
    ];

    const rows = useMemo(() => {
        if (!data) return [];

        return data.map((item) => ({
            id: item.id,
            file: (
                <a href={item.link} target="_blank">
                    {item.title}
                </a>
            ),
            action: (
                <Button small variant="danger" loading={deletingIds.includes(item.id)} onClick={() => handleDelete(item.id)}>
                    {__("Delete", "antimanual")}
                </Button>
            ),
        }));
    }, [data, deletingIds]);

    const handleFileChange = (file?: File) => {
        setPdf(file);
    };

    const handlePdfSubmit = async (file?: File) => {
        if (!file) {
            toast.error(__("Please select a PDF file to upload.", "antimanual"));
            return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            await api.post("/knowledge-base/pdf", formData);
            await refetch();

            toast.success(__("PDF content has been added to the knowledge base.", "antimanual"));
            handleFileChange(undefined);
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <div className={css("kb-option pdf-kb")}>
            <div className={css("flex items-end gap-2 mb-8")}>
                <div className={css("min-w-sm")}>
                    <FileInput
                        handleFileChange={(files) => handleFileChange(files?.[0])}
                        label={__("Upload PDF", "antimanual")}
                        accept="application/pdf"
                    />
                </div>
                <Button
                    onClick={() => {
                        if (!IS_PRO) {
                            showProAlert(
                                __("Premium Feature.", "antimanual"),
                                __("PDF upload is a Premium feature. Upgrade to Pro to upload PDF files to the knowledge base.", "antimanual")
                            );
                            return;
                        }

                        handlePdfSubmit(pdf);
                    }}
                    className={css("w-auto")}
                    loading={submitting}
                    disabled={!pdf || submitting}
                >
                    {__("Submit", "antimanual")}
                </Button>
            </div>
            <Table
                rowsLoading={isLoading}
                columns={columns}
                rows={rows}
                actions={[{ value: "delete", label: __("Delete", "antimanual"), handler: handleBulkDelete }]}
                selectedAction="delete"
            />
        </div>
    );
}
